import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Slider,
  Grid,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Chip,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress
} from '@mui/material';
import {
  Radar,
  Bar,
  Line,
  Doughnut,
  Bubble,
  Scatter
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  BubbleController,
  ScatterController
} from 'chart.js';
import {
  Info as InfoIcon,
  ArrowUpward as ArrowUpwardIcon,
  ShowChart as ShowChartIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  AutoGraph as AutoGraphIcon,
  BubbleChart as BubbleChartIcon,
  ScatterPlot as ScatterPlotIcon,
  TableChart as TableChartIcon,
  DonutLarge as DonutLargeIcon,
  CompareArrows as CompareArrowsIcon,
  TrendingUp as TrendingUpIcon,
  Dataset as DatasetIcon,
  FilterAlt as FilterAltIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  HelpOutline as HelpOutlineIcon,
  GroupWork as GroupWorkIcon,
  Layers as LayersIcon,
  AccountTree as AccountTreeIcon,
  Schema as SchemaIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';
import * as d3 from 'd3';
import { CSVLink } from 'react-csv';
import dimensionsData from './dimensionsData';

import {
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

// Register ChartJS components
ChartJS.register(
  RadialLinearScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  ChartTooltip,
  Legend,
  CategoryScale,
  BubbleController,
  ScatterController
);

// Enhanced animated number with formatting
const AnimatedNumber = ({ value, format = 'number', precision = 0 }) => {
  const { number } = useSpring({
    from: { number: 0 },
    to: { number: value },
    config: { duration: 1500 }
  });

  return (
    <animated.span>
      {number.to(n => {
        const num = parseFloat(n.toFixed(precision));
        if (format === 'currency') return `$${num.toLocaleString()}`;
        if (format === 'percentage') return `${num}%`;
        return num.toLocaleString();
      })}
    </animated.span>
  );
};

// Enhanced tooltip with rich content
const DimensionTooltip = ({ title, description, impact, unit }) => (
  <Tooltip 
    title={
      <Box sx={{ p: 2, maxWidth: 300 }}>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        <Typography variant="body2" paragraph>{description}</Typography>
        {impact !== undefined && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption">Impact Analysis ({unit}):</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Box sx={{ 
                width: '100%',
                height: 6,
                bgcolor: 'divider',
                borderRadius: 3,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  width: `${Math.min(100, Math.max(0, impact))}%`,
                  height: '100%',
                  bgcolor: impact > 70 ? 'success.main' : impact > 30 ? 'warning.main' : 'error.main'
                }} />
              </Box>
              <Typography variant="caption" sx={{ ml: 1 }}>{impact}%</Typography>
            </Box>
          </Box>
        )}
      </Box>
    } 
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'background.paper',
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider'
        }
      }
    }}
  >
    <span>
      {title} <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
    </span>
  </Tooltip>
);

// 3D bubble chart component
const BubbleChart = ({ data }) => {
  const bubbleData = {
    datasets: data.dimensions.map((dim, i) => ({
      label: dim.name,
      data: [{
        x: data.currentLevel.values[i],
        y: data.targetLevel.values[i],
        r: Math.abs(data.targetLevel.values[i] - data.currentLevel.values[i]) / 2 + 5
      }],
      backgroundColor: `hsla(${(i * 360 / data.dimensions.length)}, 70%, 60%, 0.7)`,
      borderColor: `hsla(${(i * 360 / data.dimensions.length)}, 70%, 40%, 1)`,
      borderWidth: 1
    }))
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: { 
          display: true, 
          text: 'Current State',
          font: { weight: 'bold' }
        },
        min: 0,
        max: 100,
        ticks: { stepSize: 20 }
      },
      y: {
        title: { 
          display: true, 
          text: 'Target State',
          font: { weight: 'bold' }
        },
        min: 0,
        max: 100,
        ticks: { stepSize: 20 }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const dim = data.dimensions[context.datasetIndex];
            return [
              `Dimension: ${dim.name}`,
              `Current: ${context.parsed.x}%`,
              `Target: ${context.parsed.y}%`,
              `Gap: ${Math.round(context.parsed.r * 2)}%`,
              `Unit: ${dim.unit}`
            ];
          }
        }
      },
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          padding: 20,
          font: { weight: 'bold' }
        }
      }
    }
  };

  return <Bubble data={bubbleData} options={options} />;
};

// Network graph component
const NetworkGraph = ({ data }) => {
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    
    // Simulate complex network calculation
    const timer = setTimeout(() => {
      const nodes = data.dimensions.map((dim, i) => ({
        id: dim.name,
        group: i % 3,
        value: Math.abs(data.targetLevel.values[i] - data.currentLevel.values[i]),
        current: data.currentLevel.values[i],
        target: data.targetLevel.values[i]
      }));
      
      const links = [];
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          if (Math.random() > 0.7) {
            links.push({
              source: nodes[i].id,
              target: nodes[j].id,
              value: Math.min(nodes[i].value, nodes[j].value) / 20
            });
          }
        }
      }
      
      setDimensions({ nodes, links });
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      bgcolor: 'background.paper',
      borderRadius: 2,
      p: 2
    }}>
      <Typography variant="body1" color="text.secondary">
        Network visualization would render here with {dimensions.nodes.length} nodes and {dimensions.links.length} connections
      </Typography>
    </Box>
  );
};

// Enhanced heatmap component
const HeatmapChart = ({ data }) => {
  const heatmapData = {
    labels: data.dimensions.map(d => d.name),
    datasets: [
      {
        label: 'Current State',
        data: data.currentLevel.values,
        backgroundColor: 'rgba(99, 102, 241, 0.7)'
      },
      {
        label: 'Target State',
        data: data.targetLevel.values,
        backgroundColor: 'rgba(16, 185, 129, 0.7)'
      },
      {
        label: 'Improvement Gap',
        data: data.improvementPotential,
        backgroundColor: 'rgba(245, 158, 11, 0.7)'
      }
    ]
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { 
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 20,
          font: { weight: 'bold' }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const dimIndex = context.dataIndex;
            const dim = data.dimensions[dimIndex];
            return [
              `${context.dataset.label}: ${context.raw}%`,
              `Dimension: ${dim.name}`,
              `Unit: ${dim.unit}`,
              `Description: ${dim.description}`
            ];
          }
        }
      }
    },
    scales: {
      x: { 
        stacked: true,
        grid: { display: false }
      },
      y: { 
        stacked: true,
        ticks: { font: { weight: 'bold' } }
      }
    }
  };

  return <Bar data={heatmapData} options={options} />;
};

// Sankey diagram placeholder
const SankeyDiagram = ({ data }) => {
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      bgcolor: 'background.paper',
      borderRadius: 2,
      p: 2
    }}>
      <Typography variant="body1" color="text.secondary">
        Interactive Sankey diagram would visualize flow between {data.dimensions.length} dimensions
      </Typography>
    </Box>
  );
};

// Enhanced radar with gradient fill
const EnhancedRadar = ({ data, options }) => {
  return (
    <Radar 
      data={data}
      options={options}
    />
  );
};

const MaturityComparisonDashboard = () => {
  const theme = useTheme();
  const [selectedFactor, setSelectedFactor] = useState(dimensionsData.factors[0]);
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [targetLevelIndex, setTargetLevelIndex] = useState(selectedFactor.levels.length - 1);
  const [animationKey, setAnimationKey] = useState(0);
  const [viewMode, setViewMode] = useState("radar"); // 'radar', 'bar', 'bubble', 'heatmap', 'network', 'sankey'
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filter factors based on search term
  const filteredFactors = useMemo(() => {
    return dimensionsData.factors.filter(factor => 
      factor.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const currentLevel = selectedFactor.levels[currentLevelIndex];
  const targetLevel = selectedFactor.levels[targetLevelIndex];

  // Calculate metrics
  const improvementPotential = currentLevel.values.map(
    (val, i) => targetLevel.values[i] - val
  );
  const totalImprovementPotential = improvementPotential.reduce((a, b) => a + b, 0);
  const avgImprovement = totalImprovementPotential / improvementPotential.length;
  const maxDimensionIndex = improvementPotential.indexOf(Math.max(...improvementPotential));
  const minDimensionIndex = improvementPotential.indexOf(Math.min(...improvementPotential));

  // Enhanced radar data with gradient fill
  const radarData = {
    labels: selectedFactor.dimensions.map(d => d.name),
    datasets: [
      {
        label: 'Current State',
        data: currentLevel.values,
        backgroundColor: 'rgba(99, 102, 241, 0.2)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 3,
        pointBackgroundColor: 'rgba(99, 102, 241, 1)',
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      },
      {
        label: 'Target State',
        data: targetLevel.values,
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 3,
        pointBackgroundColor: 'rgba(16, 185, 129, 1)',
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }
    ]
  };

  // Enhanced bar data with animation
  const barData = {
    labels: selectedFactor.dimensions.map(d => d.name),
    datasets: [
      {
        label: 'Current',
        data: currentLevel.values,
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(99, 102, 241, 1)'
      },
      {
        label: 'Target',
        data: targetLevel.values,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(16, 185, 129, 1)'
      }
    ]
  };

  // Chart options with enhanced interactivity
  const radarOptions = {
    responsive: true,
    scales: {
      r: {
        angleLines: { display: true, color: 'rgba(0, 0, 0, 0.05)' },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: { 
          stepSize: 20, 
          backdropColor: 'transparent',
          color: theme.palette.text.secondary
        },
        pointLabels: {
          font: { size: 11, weight: 'bold' },
          color: theme.palette.text.primary,
          callback: (value) => value.length > 15 ? `${value.substring(0, 15)}...` : value
        }
      }
    },
    plugins: {
      legend: { 
        position: 'top', 
        labels: { 
          boxWidth: 12, 
          padding: 20,
          font: { weight: 'bold' }
        } 
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => {
            const dimIndex = context.dataIndex;
            const dim = selectedFactor.dimensions[dimIndex];
            return [
              `${dim.name}: ${context.raw}%`,
              `Description: ${dim.description}`,
              `Unit: ${dim.unit}`,
              `Improvement: +${improvementPotential[dimIndex]}% possible`
            ];
          }
        }
      }
    },
    elements: {
      line: {
        tension: 0.1
      }
    }
  };

  const barOptions = {
    indexAxis: 'y',
    scales: {
      x: { 
        beginAtZero: true, 
        max: 100, 
        grid: { 
          drawBorder: false,
          color: theme.palette.divider
        },
        ticks: {
          color: theme.palette.text.secondary
        }
      },
      y: { 
        grid: { display: false },
        ticks: {
          color: theme.palette.text.primary,
          font: { weight: 'bold' }
        }
      }
    },
    plugins: {
      legend: { 
        position: 'top', 
        labels: { 
          boxWidth: 12, 
          padding: 20,
          font: { weight: 'bold' }
        } 
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => {
            const dimIndex = context.dataIndex;
            const dim = selectedFactor.dimensions[dimIndex];
            return [
              `${context.dataset.label}: ${context.raw}%`,
              `Dimension: ${dim.name}`,
              `Unit: ${dim.unit}`,
              `Improvement: +${improvementPotential[dimIndex]}% possible`
            ];
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 2000
    }
  };

  const handleFactorChange = (factor) => {
    setLoading(true);
    setTimeout(() => {
      setSelectedFactor(factor);
      setCurrentLevelIndex(0);
      setTargetLevelIndex(factor.levels.length - 1);
      setAnimationKey(prev => prev + 1);
      setLoading(false);
    }, 300);
  };

  const formatSliderValue = (value) => {
    return selectedFactor.levels[value].label;
  };

  const toggleFavorite = (factorId) => {
    if (favorites.includes(factorId)) {
      setFavorites(favorites.filter(id => id !== factorId));
    } else {
      setFavorites([...favorites, factorId]);
    }
  };

  // Prepare data for export
  const exportData = selectedFactor.dimensions.map((dim, i) => ({
    Dimension: dim.name,
    'Current State': currentLevel.values[i],
    'Target State': targetLevel.values[i],
    'Improvement Potential': improvementPotential[i],
    'Unit': dim.unit,
    'Description': dim.description
  }));

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)'
      }}>
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 2,
            ease: "easeInOut"
          }}
        >
          <AutoGraphIcon sx={{ fontSize: 80, color: 'primary.main' }} />
        </motion.div>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 4 }, 
      maxWidth: 1800, 
      margin: '0 auto',
      transform: `scale(${zoomLevel})`,
      transformOrigin: 'top center',
      transition: 'transform 0.3s ease'
    }}>
      <Card elevation={0} sx={{ 
        borderRadius: 4,
        background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        {/* Header */}
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ 
                bgcolor: 'primary.main', 
                color: 'primary.contrastText',
                mr: 2
              }}>
                <AssessmentIcon />
              </Avatar>
              <Typography variant="h4" component="div" sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(90deg, #4f46e5, #06b6d4)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px'
              }}>
                Advanced Maturity Impact Analyzer
              </Typography>
            </Box>
          }
          subheader={
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {selectedFactor.description || 'Comprehensive analysis of maturity dimensions and their business impacts'}
            </Typography>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Radar View">
                <IconButton 
                  onClick={() => setViewMode('radar')} 
                  color={viewMode === 'radar' ? 'primary' : 'default'}
                  sx={{ 
                    bgcolor: viewMode === 'radar' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)' }
                  }}
                >
                  <ShowChartIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bar View">
                <IconButton 
                  onClick={() => setViewMode('bar')} 
                  color={viewMode === 'bar' ? 'primary' : 'default'}
                  sx={{ 
                    bgcolor: viewMode === 'bar' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)' }
                  }}
                >
                  <TimelineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bubble Chart">
                <IconButton 
                  onClick={() => setViewMode('bubble')} 
                  color={viewMode === 'bubble' ? 'primary' : 'default'}
                  sx={{ 
                    bgcolor: viewMode === 'bubble' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)' }
                  }}
                >
                  <BubbleChartIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Heatmap View">
                <IconButton 
                  onClick={() => setViewMode('heatmap')} 
                  color={viewMode === 'heatmap' ? 'primary' : 'default'}
                  sx={{ 
                    bgcolor: viewMode === 'heatmap' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)' }
                  }}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Network View">
                <IconButton 
                  onClick={() => setViewMode('network')} 
                  color={viewMode === 'network' ? 'primary' : 'default'}
                  sx={{ 
                    bgcolor: viewMode === 'network' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)' }
                  }}
                >
                  <AccountTreeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sankey Diagram">
                <IconButton 
                  onClick={() => setViewMode('sankey')} 
                  color={viewMode === 'sankey' ? 'primary' : 'default'}
                  sx={{ 
                    bgcolor: viewMode === 'sankey' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)' }
                  }}
                >
                  <SchemaIcon />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Tooltip title="Zoom In">
                <IconButton 
                  onClick={() => setZoomLevel(Math.min(1.5, zoomLevel + 0.1))}
                  disabled={zoomLevel >= 1.5}
                >
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton 
                  onClick={() => setZoomLevel(Math.max(0.7, zoomLevel - 0.1))}
                  disabled={zoomLevel <= 0.7}
                >
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Settings">
                <IconButton onClick={() => setSettingsOpen(true)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
          sx={{ 
            bgcolor: 'white',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
            pt: 3,
            pb: 2,
            px: { xs: 2, md: 4 }
          }}
        />
        
        <CardContent>
          <Grid container spacing={3}>
            {/* Factor Selection */}
            <Grid item xs={12} md={3}>
              <Paper elevation={0} sx={{ 
                p: 2, 
                borderRadius: 3,
                height: '100%',
                background: 'white',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search factors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    size="small"
                  />
                </Box>
                
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{ mb: 2 }}
                  variant="fullWidth"
                >
                  <Tab label="All" />
                  <Tab label="Favorites" />
                </Tabs>
                
                <Box sx={{ 
                  height: 'calc(100vh - 300px)', 
                  overflowY: 'auto',
                  pr: 1
                }}>
                  <List dense>
                    {(activeTab === 0 ? filteredFactors : filteredFactors.filter(f => favorites.includes(f.id)))
                      .map((factor) => (
                      <motion.div 
                        key={factor.id}
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ListItem 
                          button
                          selected={selectedFactor.id === factor.id}
                          onClick={() => handleFactorChange(factor)}
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            bgcolor: selectedFactor.id === factor.id ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                            '&:hover': { 
                              bgcolor: selectedFactor.id === factor.id ? 
                                'rgba(99, 102, 241, 0.15)' : 'rgba(0, 0, 0, 0.03)' 
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Avatar sx={{ 
                              bgcolor: 'rgba(99, 102, 241, 0.1)', 
                              color: 'primary.main',
                              width: 28,
                              height: 28
                            }}>
                              {factor.icon || <AssessmentIcon />}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {factor.name.split(' - ')[1]}
                              </Typography>
                            } 
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {factor.name.split(' - ')[0]}
                              </Typography>
                            }
                            sx={{ my: 0 }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(factor.id);
                            }}
                            sx={{ ml: 1 }}
                          >
                            {favorites.includes(factor.id) ? (
                              <StarIcon color="warning" fontSize="small" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </ListItem>
                      </motion.div>
                    ))}
                  </List>
                </Box>
              </Paper>
            </Grid>
            
            {/* Main Content */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={3}>
                {/* Controls */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: 'white',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Current State: <strong>{currentLevel.label}</strong>
                          </Typography>
                          <Tooltip title={currentLevel.description} arrow>
                            <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                          </Tooltip>
                        </Box>
                        <Slider
                          value={currentLevelIndex}
                          onChange={(e, newValue) => setCurrentLevelIndex(newValue)}
                          min={0}
                          max={selectedFactor.levels.length - 1}
                          step={1}
                          valueLabelFormat={formatSliderValue}
                          valueLabelDisplay="auto"
                          marks
                          sx={{
                            color: '#6366f1',
                            '& .MuiSlider-valueLabel': {
                              backgroundColor: '#6366f1',
                              fontSize: 12,
                              fontWeight: 'bold',
                              padding: '4px 8px'
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={5}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Target State: <strong>{targetLevel.label}</strong>
                          </Typography>
                          <Tooltip title={targetLevel.description} arrow>
                            <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                          </Tooltip>
                        </Box>
                        <Slider
                          value={targetLevelIndex}
                          onChange={(e, newValue) => setTargetLevelIndex(newValue)}
                          min={0}
                          max={selectedFactor.levels.length - 1}
                          step={1}
                          valueLabelFormat={formatSliderValue}
                          valueLabelDisplay="auto"
                          marks
                          sx={{
                            color: '#10b981',
                            '& .MuiSlider-valueLabel': {
                              backgroundColor: '#10b981',
                              fontSize: 12,
                              fontWeight: 'bold',
                              padding: '4px 8px'
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={2} sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        gap: 1
                      }}>
                        <Tooltip title="Reset Selections">
                          <IconButton 
                            onClick={() => {
                              setCurrentLevelIndex(0);
                              setTargetLevelIndex(selectedFactor.levels.length - 1);
                            }}
                            color="primary"
                            sx={{ 
                              bgcolor: 'rgba(99, 102, 241, 0.1)',
                              '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' }
                            }}
                          >
                            <ArrowUpwardIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Export Data">
                          <CSVLink 
                            data={exportData} 
                            filename={`maturity-analysis-${selectedFactor.name}.csv`}
                            style={{ textDecoration: 'none' }}
                          >
                            <IconButton 
                              color="primary"
                              sx={{ 
                                bgcolor: 'rgba(99, 102, 241, 0.1)',
                                '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' }
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </CSVLink>
                        </Tooltip>
                        <Tooltip title="Share Analysis">
                          <IconButton 
                            color="primary"
                            sx={{ 
                              bgcolor: 'rgba(99, 102, 241, 0.1)',
                              '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' }
                            }}
                          >
                            <ShareIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                
                {/* Key Metrics */}
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <motion.div whileHover={{ y: -5 }}>
                        <Paper elevation={0} sx={{ 
                          p: 3, 
                          borderRadius: 3,
                          height: '100%',
                          background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                          border: '1px solid rgba(0,0,0,0.05)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TrendingUpIcon sx={{ mr: 1, color: '#6366f1' }} />
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                              Improvement Potential
                            </Typography>
                          </Box>
                          <Typography variant="h2" sx={{ fontWeight: 800, mb: 1 }}>
                            <AnimatedNumber value={totalImprovementPotential} /> pts
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Across all dimensions
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body1">
                            Average improvement: <strong>{avgImprovement.toFixed(1)} pts</strong>
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <motion.div whileHover={{ y: -5 }}>
                        <Paper elevation={0} sx={{ 
                          p: 3, 
                          borderRadius: 3,
                          height: '100%',
                          background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                          border: '1px solid rgba(0,0,0,0.05)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <StarIcon sx={{ mr: 1, color: '#10b981' }} />
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                              Highest Impact Dimension
                            </Typography>
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                            {selectedFactor.dimensions[maxDimensionIndex]?.name || 'N/A'}
                          </Typography>
                          <Typography variant="h2" sx={{ fontWeight: 800, color: '#10b981' }}>
                            +<AnimatedNumber value={improvementPotential[maxDimensionIndex] || 0} />%
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            {selectedFactor.dimensions[maxDimensionIndex]?.description || ''}
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <motion.div whileHover={{ y: -5 }}>
                        <Paper elevation={0} sx={{ 
                          p: 3, 
                          borderRadius: 3,
                          height: '100%',
                          background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                          border: '1px solid rgba(0,0,0,0.05)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <WarningIcon sx={{ mr: 1, color: '#ef4444' }} />
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                              Lowest Impact Dimension
                            </Typography>
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                            {selectedFactor.dimensions[minDimensionIndex]?.name || 'N/A'}
                          </Typography>
                          <Typography variant="h2" sx={{ fontWeight: 800, color: '#ef4444' }}>
                            +<AnimatedNumber value={improvementPotential[minDimensionIndex] || 0} />%
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            {selectedFactor.dimensions[minDimensionIndex]?.description || ''}
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Main Visualization */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    height: '500px',
                    background: 'white',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    position: 'relative'
                  }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`${viewMode}-${animationKey}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        style={{ height: '100%' }}
                      >
                        {viewMode === 'radar' && (
                          <EnhancedRadar 
                            data={radarData} 
                            options={radarOptions} 
                          />
                        )}
                        {viewMode === 'bar' && (
                          <Bar 
                            data={barData} 
                            options={barOptions} 
                          />
                        )}
                        {viewMode === 'bubble' && (
                          <BubbleChart 
                            data={{
                              dimensions: selectedFactor.dimensions,
                              currentLevel,
                              targetLevel
                            }} 
                          />
                        )}
                        {viewMode === 'heatmap' && (
                          <HeatmapChart 
                            data={{
                              dimensions: selectedFactor.dimensions,
                              currentLevel,
                              targetLevel,
                              improvementPotential
                            }} 
                          />
                        )}
                        {viewMode === 'network' && (
                          <NetworkGraph 
                            data={{
                              dimensions: selectedFactor.dimensions,
                              currentLevel,
                              targetLevel
                            }} 
                          />
                        )}
                        {viewMode === 'sankey' && (
                          <SankeyDiagram 
                            data={{
                              dimensions: selectedFactor.dimensions,
                              currentLevel,
                              targetLevel
                            }} 
                          />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </Paper>
                </Grid>
                
                {/* Dimension Details */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: 'white',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Dimension Analysis
                    </Typography>
                    <Grid container spacing={3}>
                      {selectedFactor.dimensions.map((dim, index) => (
                        <Grid item xs={12} sm={6} md={4} key={dim.name}>
                          <motion.div whileHover={{ scale: 1.02 }}>
                            <Card elevation={0} sx={{ 
                              p: 2, 
                              borderRadius: 2,
                              height: '100%',
                              border: '1px solid rgba(0,0,0,0.05)',
                              background: improvementPotential[index] > 0 ? 
                                'rgba(16, 185, 129, 0.03)' : 'rgba(239, 68, 68, 0.03)'
                            }}>
                              <CardHeader
                                avatar={
                                  <Avatar sx={{ 
                                    bgcolor: improvementPotential[index] > 0 ? 
                                      'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    color: improvementPotential[index] > 0 ? 
                                      '#10b981' : '#ef4444'
                                  }}>
                                    {dim.icon || <AssessmentIcon />}
                                  </Avatar>
                                }
                                title={
                                  <DimensionTooltip 
                                    title={dim.name} 
                                    description={dim.description} 
                                    impact={improvementPotential[index]} 
                                    unit={dim.unit}
                                  />
                                }
                                subheader={
                                  <Typography variant="caption" color="text.secondary">
                                    {dim.category}
                                  </Typography>
                                }
                                sx={{ p: 0, mb: 1 }}
                              />
                              <CardContent sx={{ p: 0 }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  mb: 1
                                }}>
                                  <Typography variant="body2" sx={{ mr: 1 }}>
                                    Current:
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={currentLevel.values[index]} 
                                    sx={{ 
                                      flexGrow: 1,
                                      height: 8,
                                      borderRadius: 4,
                                      bgcolor: 'rgba(99, 102, 241, 0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: '#6366f1'
                                      }
                                    }} 
                                  />
                                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                                    {currentLevel.values[index]}%
                                  </Typography>
                                </Box>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  mb: 1
                                }}>
                                  <Typography variant="body2" sx={{ mr: 1 }}>
                                    Target:
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={targetLevel.values[index]} 
                                    sx={{ 
                                      flexGrow: 1,
                                      height: 8,
                                      borderRadius: 4,
                                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: '#10b981'
                                      }
                                    }} 
                                  />
                                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                                    {targetLevel.values[index]}%
                                  </Typography>
                                </Box>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  mb: 1
                                }}>
                                  <Typography variant="body2" sx={{ mr: 1 }}>
                                    Gap:
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={Math.abs(improvementPotential[index])} 
                                    sx={{ 
                                      flexGrow: 1,
                                      height: 8,
                                      borderRadius: 4,
                                      bgcolor: 'rgba(245, 158, 11, 0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: improvementPotential[index] > 0 ? 
                                          '#f59e0b' : '#ef4444'
                                      }
                                    }} 
                                  />
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      ml: 1, 
                                      fontWeight: 600,
                                      color: improvementPotential[index] > 0 ? 
                                        '#10b981' : '#ef4444'
                                    }}
                                  >
                                    {improvementPotential[index] > 0 ? '+' : ''}
                                    {improvementPotential[index]}%
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Unit: {dim.unit}
                                </Typography>
                              </CardContent>
                            </Card>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog 
        open={settingsOpen} 
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            Dashboard Settings
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>
            Visualization Preferences
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Default View Mode</InputLabel>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              label="Default View Mode"
            >
              <MenuItem value="radar">Radar Chart</MenuItem>
              <MenuItem value="bar">Bar Chart</MenuItem>
              <MenuItem value="bubble">Bubble Chart</MenuItem>
              <MenuItem value="heatmap">Heatmap</MenuItem>
              <MenuItem value="network">Network Graph</MenuItem>
              <MenuItem value="sankey">Sankey Diagram</MenuItem>
            </Select>
          </FormControl>
          
          <Typography variant="subtitle1" gutterBottom>
            Animation Settings
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              Transition Duration: {zoomLevel.toFixed(1)}x
            </Typography>
            <Slider
              value={zoomLevel}
              onChange={(e, newValue) => setZoomLevel(newValue)}
              min={0.7}
              max={1.5}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value.toFixed(1)}x`}
            />
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>
            Data Preferences
          </Typography>
          <FormControlLabel
            control={<Switch checked={true} />}
            label="Show tooltips by default"
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={<Switch checked={false} />}
            label="Enable predictive analytics"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => setSettingsOpen(false)}
            variant="contained"
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export {
  EnhancedRadar,
  DimensionTooltip,
  AnimatedNumber
};